export interface Coordinates {
  lat: number;
  lng: number;
}

export type ServiceZoneAvailability = "available" | "coming-soon" | "unavailable";

export interface ServiceZone {
  id: string;
  cityName: string;
  zoneName: string;
  /** Simple illustrative polygon around the zone center — not a survey-accurate boundary. */
  boundary: Coordinates[];
  availability: ServiceZoneAvailability;
  typicalDeliveryWindow?: string;
}

export type HubStatus = "live" | "opening-soon";

export interface Hub {
  id: string;
  name: string;
  cityName: string;
  location: Coordinates;
  /** Locality-level only — never a specific street address (see AC's own "general address" wording). */
  generalAddress: string;
  status: HubStatus;
}

export interface Harbour {
  id: string;
  name: string;
  region: string;
  location: Coordinates;
  sourcingStory: string;
  speciesTypicallySourced: string[];
  certifiedSustainable: boolean;
}

export interface ExpansionCity {
  id: string;
  cityName: string;
  state: string;
  location: Coordinates;
  estimatedLaunchQuarter?: string;
}

export type MapSelection =
  | { kind: "zone"; data: ServiceZone }
  | { kind: "hub"; data: Hub }
  | { kind: "harbour"; data: Harbour }
  | { kind: "expansion"; data: ExpansionCity };

function box(center: Coordinates, deltaLat: number, deltaLng: number): Coordinates[] {
  return [
    { lat: center.lat - deltaLat, lng: center.lng - deltaLng },
    { lat: center.lat - deltaLat, lng: center.lng + deltaLng },
    { lat: center.lat + deltaLat, lng: center.lng + deltaLng },
    { lat: center.lat + deltaLat, lng: center.lng - deltaLng },
  ];
}

const MUMBAI: Coordinates = { lat: 19.076, lng: 72.8777 };
const SURAT: Coordinates = { lat: 21.1702, lng: 72.8311 };
const GOA: Coordinates = { lat: 15.4909, lng: 73.8278 };
const MANGALORE: Coordinates = { lat: 12.9141, lng: 74.856 };
const KOZHIKODE: Coordinates = { lat: 11.2588, lng: 75.7804 };
const KOCHI: Coordinates = { lat: 9.9312, lng: 76.2673 };
const ALAPPUZHA: Coordinates = { lat: 9.4981, lng: 76.3388 };
const THIRUVANANTHAPURAM: Coordinates = { lat: 8.5241, lng: 76.9366 };
const KANNUR: Coordinates = { lat: 11.8745, lng: 75.3704 };
const CHENNAI: Coordinates = { lat: 13.0827, lng: 80.2707 };
const PUDUCHERRY: Coordinates = { lat: 11.9416, lng: 79.8083 };
const VISAKHAPATNAM: Coordinates = { lat: 17.6868, lng: 83.2185 };

export const SERVICE_ZONES: ServiceZone[] = [
  { id: "mumbai-central", cityName: "Mumbai", zoneName: "Mumbai Central", boundary: box(MUMBAI, 0.08, 0.08), availability: "available", typicalDeliveryWindow: "6–9 PM same day" },
  { id: "surat-central", cityName: "Surat", zoneName: "Surat Central", boundary: box(SURAT, 0.06, 0.06), availability: "available", typicalDeliveryWindow: "5–8 PM same day" },
  { id: "goa-panaji", cityName: "Goa", zoneName: "Panaji & Coastal Belt", boundary: box(GOA, 0.07, 0.07), availability: "available", typicalDeliveryWindow: "6–9 PM same day" },
  { id: "mangalore-central", cityName: "Mangalore", zoneName: "Mangalore Central", boundary: box(MANGALORE, 0.06, 0.06), availability: "available", typicalDeliveryWindow: "5–8 PM same day" },
  { id: "kozhikode-central", cityName: "Kozhikode", zoneName: "Kozhikode Central", boundary: box(KOZHIKODE, 0.06, 0.06), availability: "available", typicalDeliveryWindow: "6–9 PM same day" },
  { id: "kochi-central", cityName: "Kochi", zoneName: "Kochi & Kakkanad", boundary: box(KOCHI, 0.08, 0.08), availability: "available", typicalDeliveryWindow: "5–8 PM same day" },
  { id: "alappuzha-central", cityName: "Alappuzha", zoneName: "Alappuzha Central", boundary: box(ALAPPUZHA, 0.05, 0.05), availability: "available", typicalDeliveryWindow: "6–9 PM same day" },
  { id: "tvm-central", cityName: "Thiruvananthapuram", zoneName: "Thiruvananthapuram Central", boundary: box(THIRUVANANTHAPURAM, 0.06, 0.06), availability: "available", typicalDeliveryWindow: "5–8 PM same day" },
  { id: "kannur-central", cityName: "Kannur", zoneName: "Kannur Central", boundary: box(KANNUR, 0.05, 0.05), availability: "available", typicalDeliveryWindow: "6–9 PM same day" },
  { id: "chennai-central", cityName: "Chennai", zoneName: "Chennai & Adyar", boundary: box(CHENNAI, 0.08, 0.08), availability: "available", typicalDeliveryWindow: "5–8 PM same day" },
  { id: "puducherry-central", cityName: "Puducherry", zoneName: "Puducherry Central", boundary: box(PUDUCHERRY, 0.05, 0.05), availability: "available", typicalDeliveryWindow: "6–9 PM same day" },
  { id: "vizag-central", cityName: "Visakhapatnam", zoneName: "Visakhapatnam Central", boundary: box(VISAKHAPATNAM, 0.07, 0.07), availability: "available", typicalDeliveryWindow: "5–8 PM same day" },
];

export const HUBS: Hub[] = [
  { id: "hub-mumbai", name: "Mumbai Hub", cityName: "Mumbai", location: { lat: 19.06, lng: 72.87 }, generalAddress: "Near Lower Parel, Mumbai", status: "live" },
  { id: "hub-surat", name: "Surat Hub", cityName: "Surat", location: { lat: 21.165, lng: 72.83 }, generalAddress: "Near Adajan, Surat", status: "opening-soon" },
  { id: "hub-goa", name: "Goa Hub", cityName: "Goa", location: { lat: 15.5, lng: 73.83 }, generalAddress: "Near Panaji, Goa", status: "live" },
  { id: "hub-mangalore", name: "Mangalore Hub", cityName: "Mangalore", location: { lat: 12.91, lng: 74.85 }, generalAddress: "Near Bunder, Mangalore", status: "live" },
  { id: "hub-kozhikode", name: "Kozhikode Hub", cityName: "Kozhikode", location: { lat: 11.25, lng: 75.78 }, generalAddress: "Near Beach Road, Kozhikode", status: "live" },
  { id: "hub-kochi", name: "Kochi Hub", cityName: "Kochi", location: { lat: 9.94, lng: 76.27 }, generalAddress: "Near Fort Kochi, Kochi", status: "live" },
  { id: "hub-alappuzha", name: "Alappuzha Hub", cityName: "Alappuzha", location: { lat: 9.5, lng: 76.33 }, generalAddress: "Near Boat Jetty, Alappuzha", status: "live" },
  { id: "hub-tvm", name: "Thiruvananthapuram Hub", cityName: "Thiruvananthapuram", location: { lat: 8.53, lng: 76.94 }, generalAddress: "Near Kovalam Road, Thiruvananthapuram", status: "live" },
  { id: "hub-kannur", name: "Kannur Hub", cityName: "Kannur", location: { lat: 11.87, lng: 75.37 }, generalAddress: "Near Fort Road, Kannur", status: "opening-soon" },
  { id: "hub-chennai", name: "Chennai Hub", cityName: "Chennai", location: { lat: 13.07, lng: 80.27 }, generalAddress: "Near Besant Nagar, Chennai", status: "live" },
  { id: "hub-puducherry", name: "Puducherry Hub", cityName: "Puducherry", location: { lat: 11.94, lng: 79.81 }, generalAddress: "Near White Town, Puducherry", status: "live" },
  { id: "hub-vizag", name: "Visakhapatnam Hub", cityName: "Visakhapatnam", location: { lat: 17.68, lng: 83.21 }, generalAddress: "Near RK Beach, Visakhapatnam", status: "live" },
];

// Fictional harbour entities in the spirit of About.tsx's FISHERMEN array
// (generic composite names + broad real regions, e.g. "Konkan Coast") —
// deliberately NOT named after real, specific, independently-identifiable
// docks (an earlier draft used real harbour names like "Sassoon Dock" with
// "our partner crews have fished here for years"-style claims; that's a
// specific, checkable claim about a real place ZenFresh has no actual
// relationship with, the same class of issue this project avoids everywhere
// else — see jira/README.md's honesty-policy note). Regions match the ones
// FISHERMEN already established so the two arrays stay consistent.
export const HARBOURS: Harbour[] = [
  { id: "harbour-konkan", name: "Konkan Coast Harbour", region: "Konkan Coast", location: { lat: 18.9, lng: 72.85 }, sourcingStory: "A cluster of small-boat fishing docks along the Konkan coast where several of our partner crews bring in their daily catch.", speciesTypicallySourced: ["Pomfret", "Mackerel", "Prawns"], certifiedSustainable: true },
  { id: "harbour-karnataka", name: "Karnataka Coast Co-operative", region: "Karnataka Coast", location: { lat: 13.2, lng: 74.75 }, sourcingStory: "A trawling co-operative on the Karnataka coast, a steady source of squid and mackerel.", speciesTypicallySourced: ["Squid", "Mackerel", "Anchovy"], certifiedSustainable: true },
  { id: "harbour-malabar", name: "Malabar Fishing Harbour", region: "Malabar Coast", location: { lat: 9.9, lng: 76.22 }, sourcingStory: "A busy Kerala backwater harbour where pearl spot, prawns, and lobster typically come ashore.", speciesTypicallySourced: ["Karimeen", "Tiger Prawns", "Spiny Lobster"], certifiedSustainable: true },
  { id: "harbour-coromandel", name: "Coromandel Coast Harbour", region: "Coromandel Coast", location: { lat: 13.05, lng: 80.28 }, sourcingStory: "A Tamil Nadu coastal harbour supplying kingfish and snapper to our Coromandel Coast crews.", speciesTypicallySourced: ["Vanjaram Kingfish", "Red Snapper"], certifiedSustainable: false },
  { id: "harbour-andhra", name: "Andhra Coast Harbour", region: "Andhra Coast", location: { lat: 17.65, lng: 83.25 }, sourcingStory: "A newer sourcing point on the Andhra coast, mainly for mud crab and Indian mackerel.", speciesTypicallySourced: ["Mud Crab", "Indian Mackerel"], certifiedSustainable: false },
  { id: "harbour-north-malabar", name: "North Malabar Harbour", region: "Malabar Coast", location: { lat: 11.85, lng: 75.33 }, sourcingStory: "A smaller, close-knit harbour further up the Malabar coast, home to some of our longest-standing partner families.", speciesTypicallySourced: ["Mackerel", "Silver Pomfret"], certifiedSustainable: true },
];

export const EXPANSION_CITIES: ExpansionCity[] = [
  { id: "expansion-ratnagiri", cityName: "Ratnagiri", state: "Maharashtra", location: { lat: 16.9902, lng: 73.312 }, estimatedLaunchQuarter: "Q2 2027" },
  { id: "expansion-karwar", cityName: "Karwar", state: "Karnataka", location: { lat: 14.8022, lng: 74.129 }, estimatedLaunchQuarter: "Q3 2027" },
  { id: "expansion-nagapattinam", cityName: "Nagapattinam", state: "Tamil Nadu", location: { lat: 10.7672, lng: 79.8449 } },
];

// Illustrative subset of real pincodes for the 12 currently-served cities,
// not a claim of full India-wide pincode coverage — a pincode outside this
// map is honestly "not currently serviceable" (see ServiceabilityChecker),
// same as most early-stage delivery networks' real out-of-zone behavior.
export const PINCODE_ZONES: Record<string, string> = {
  "400001": "mumbai-central",
  "400050": "mumbai-central",
  "395003": "surat-central",
  "403001": "goa-panaji",
  "575001": "mangalore-central",
  "673001": "kozhikode-central",
  "682001": "kochi-central",
  "682016": "kochi-central",
  "688001": "alappuzha-central",
  "695001": "tvm-central",
  "670001": "kannur-central",
  "600001": "chennai-central",
  "600028": "chennai-central",
  "605001": "puducherry-central",
  "530001": "vizag-central",
};
